import { useContext, useLayoutEffect, type ReactNode, type Ref } from "react";
import type { BoardAddPlacement, BoardPlacement } from "./Board.types";
import {
    getBoardAddTarget,
    getGapAddLabel,
    type BoardColumnAdding,
} from "./boardAdd";
import {
    BoardFooterAddButton,
    BoardGapAddButton,
    BoardHeaderAddButton,
} from "./BoardAddButton";
import { getCellKey, type BoardCellEntry } from "./boardCells";
import { BoardEnteringContext } from "./boardEntrance";
import { BoardSortHint, type BoardCellSortHint } from "./BoardSortHint";
import {
    captureScrollSnapshot,
    restoreScrollAnchor,
} from "./boardScrollSnapshot";
import { BoardWindowedCards } from "./BoardWindowedCards";
import { BoardScrollerContext } from "./useBoardWindow";
import styles from "./Board.module.scss";

/** What a cell hands each card's `li`. */
export interface BoardEntrySlot {
    index: number;
    /** Create buttons on the gaps around the card. */
    gapAdds: ReactNode;
    /**
     * Set in windowed cells: measures the `li`, which needs `data-index`.
     * Those cells scroll an entering card into view themselves.
     */
    measureRef?: (node: HTMLElement | null) => void;
}

export interface BoardCellWindowing {
    /**
     * Cards that stay mounted off-screen: the dragged one and the one focus
     * returns to after a drop. The focused card always stays.
     */
    pinnedKeys: readonly string[];
}

export interface BoardCellListProps<TItem> {
    placement: BoardPlacement;
    /** Ids of the column (and lane) titles naming the list. */
    labelledBy: string;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
    /** Renders one keyed `li` per entry, with `slot.gapAdds` inside it. */
    renderEntry: (
        entry: BoardCellEntry<TItem>,
        slot: BoardEntrySlot,
    ) => ReactNode;
    /** Set when the cell is long enough to mount only cards near the viewport. */
    windowing?: BoardCellWindowing;
    /** Null renders nothing for an empty cell, as lanes do at rest. */
    emptyLabel: string | null;
    adding: BoardColumnAdding<TItem> | null;
    /** Gap buttons leave while any card is dragged. */
    isDragActive?: boolean;
    /** Set while a drag is on and this cell refuses the card. */
    refusalReason?: string;
    /** Set while a sorted pointer drag is headed here: shown in its slot. */
    sortHint?: BoardCellSortHint;
    ref?: Ref<HTMLDivElement>;
}

interface BoardPlainCardsProps {
    placement: BoardPlacement;
    labelledBy: string;
    children: ReactNode;
}

/**
 * A short cell's cards, all mounted. Replacing a windowed list, it scrolls
 * the card that was first in view back to where it was.
 */
const BoardPlainCards = ({
    placement,
    labelledBy,
    children,
}: Readonly<BoardPlainCardsProps>) => {
    const { element: scroller } = useContext(BoardScrollerContext);
    const cellKey = getCellKey(placement);
    useLayoutEffect(() => {
        if (scroller === null) {
            return undefined;
        }
        restoreScrollAnchor(scroller, cellKey);
        return () =>
            captureScrollSnapshot({ scroller, kind: "plain", cellKey });
    }, [scroller, cellKey]);
    return (
        <ul aria-labelledby={labelledBy} className={styles.cards}>
            {children}
        </ul>
    );
};

/** One cell's cards with its footer and gap create buttons. */
export const BoardCellList = <TItem,>({
    placement,
    labelledBy,
    entries,
    renderEntry,
    emptyLabel,
    windowing,
    adding,
    isDragActive = false,
    refusalReason,
    sortHint,
    ref,
}: Readonly<BoardCellListProps<TItem>>) => {
    const entering = useContext(BoardEnteringContext);
    const addAt = (index: number, button: BoardAddPlacement) =>
        adding?.onAdd(getBoardAddTarget({ placement, entries, index, button }));
    const hasGapAdds =
        adding !== null && adding.placements.has("between") && !isDragActive;
    // An empty cell has no gaps; when no header + or footer bar adds there
    // either, its gap buttons collapse into one bar.
    const hasEmptyCellAdd =
        hasGapAdds &&
        entries.length === 0 &&
        adding.hasGapAboveFirst &&
        adding.hasGapBelowLast;
    const renderGapAdds = (index: number) =>
        hasGapAdds && (
            <>
                {(index > 0 || adding.hasGapAboveFirst) && (
                    <BoardGapAddButton
                        label={getGapAddLabel({ adding, entries, index })}
                        onClick={() => addAt(index, "between")}
                    />
                )}
                {index === entries.length - 1 && adding.hasGapBelowLast && (
                    <BoardGapAddButton
                        edge="below"
                        label={getGapAddLabel({
                            adding,
                            entries,
                            index: index + 1,
                        })}
                        onClick={() => addAt(index + 1, "between")}
                    />
                )}
            </>
        );

    const renderCards = () => {
        if (entries.length === 0) {
            return (
                emptyLabel !== null && (
                    <p className={styles.empty}>{emptyLabel}</p>
                )
            );
        }
        if (windowing === undefined) {
            return (
                <BoardPlainCards placement={placement} labelledBy={labelledBy}>
                    {entries.map((entry, index) =>
                        renderEntry(entry, {
                            index,
                            gapAdds: renderGapAdds(index),
                        }),
                    )}
                </BoardPlainCards>
            );
        }
        return (
            <BoardWindowedCards
                placement={placement}
                labelledBy={labelledBy}
                entries={entries}
                pinnedKeys={windowing.pinnedKeys}
                enteringIndex={
                    entering === null || entering.reveal === "none"
                        ? -1
                        : entries.findIndex(({ itemId }) =>
                              entering.pendingIds.has(itemId),
                          )
                }
                renderEntryAt={(index, measureRef) =>
                    renderEntry(entries[index], {
                        index,
                        gapAdds: renderGapAdds(index),
                        measureRef,
                    })
                }
            />
        );
    };

    return (
        <div
            ref={ref}
            className={styles.cell}
            data-refuses-drop={refusalReason !== undefined}
        >
            {renderCards()}
            {adding?.placements.has("footer") && (
                <BoardFooterAddButton
                    label={adding.label}
                    onClick={() => addAt(entries.length, "footer")}
                />
            )}
            {hasEmptyCellAdd && (
                <BoardFooterAddButton
                    label={adding.label}
                    onClick={() => addAt(0, "between")}
                />
            )}
            {sortHint !== undefined && <BoardSortHint hint={sortHint} />}
        </div>
    );
};

interface BoardHeaderAddProps<TItem> {
    /** Render only when its placements include `header`. */
    adding: BoardColumnAdding<TItem>;
    placement: BoardPlacement;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
}

/** The header `+` of a board without lanes; adds at the top of the column. */
export const BoardHeaderAdd = <TItem,>({
    adding,
    placement,
    entries,
}: Readonly<BoardHeaderAddProps<TItem>>) => (
    <BoardHeaderAddButton
        label={adding.label}
        onClick={() =>
            adding.onAdd(
                getBoardAddTarget({
                    placement,
                    entries,
                    index: 0,
                    button: "header",
                }),
            )
        }
    />
);
